import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader, Card, Button, Input, Select, Label, Textarea, Empty } from "@/components/Primitives";
import { StatusPill } from "@/components/StatusPill";
import { Plus, X, Pencil, Trash2, ExternalLink, Github, BookOpen, Server, Users, Repeat, TrendingUp, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

const KINDS = [
  { v: "saas_app", l: "SaaS app" },
  { v: "internal_tool", l: "Internal tool" },
  { v: "integration", l: "Integration" },
  { v: "other", l: "Other" },
];

export default function ProductsPage() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [allocating, setAllocating] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [rev, setRev] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const [p, c, r] = await Promise.all([
      api.get("/products"),
      api.get("/customers"),
      api.get("/stats/software-revenue").catch(() => ({ data: null })),
    ]);
    setItems(p.data || []);
    setCustomers(c.data || []);
    setRev(r.data);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"? Customers allocated will lose access in their dashboards.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const removeAllocation = async (p, alloc) => {
    if (!window.confirm(`Remove ${alloc.customer_name || "this customer"} from ${p.name}?`)) return;
    try {
      await api.delete(`/products/${p.id}/allocations/${alloc.customer_id}`);
      toast.success("Allocation removed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader breadcrumb="Studio · Products" title="Software & SaaS" subtitle="Your in-house products. Set deployment URLs, allocate to customers, and price each customer individually."
        actions={<Button onClick={() => setShowNew(true)} data-testid="new-product-btn"><Plus className="w-4 h-4" strokeWidth={2}/>Add software</Button>} />

      {rev && (
        <Card data-testid="software-revenue-strip">
          <div className="p-5 flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">MRR from software</div>
              <div className="font-mono text-3xl font-bold tracking-tight mt-0.5">{gbp(rev.totals?.mrr || 0)}<span className="text-[14px] font-normal text-[hsl(var(--text-muted))]">/mo</span></div>
            </div>
            <div className="h-10 w-px bg-[hsl(var(--border))]"/>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">ARR (projected)</div>
              <div className="font-mono text-xl font-semibold mt-0.5 text-[hsl(var(--success))]">{gbp(rev.totals?.arr || 0)}</div>
            </div>
            <div className="h-10 w-px bg-[hsl(var(--border))]"/>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Setup booked</div>
              <div className="font-mono text-xl font-semibold mt-0.5">{gbp(rev.totals?.setup_booked || 0)}</div>
            </div>
            <div className="h-10 w-px bg-[hsl(var(--border))]"/>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400 font-semibold">Pending pipeline</div>
              <div className="font-mono text-xl font-semibold mt-0.5 text-amber-600 dark:text-amber-400">{gbp(rev.totals?.pending_mrr || 0)}<span className="text-[12px] font-normal text-[hsl(var(--text-muted))]">/mo</span></div>
            </div>
            <div className="h-10 w-px bg-[hsl(var(--border))]"/>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Active subs</div>
              <div className="font-mono text-xl font-semibold mt-0.5">{rev.totals?.active_subscriptions || 0} <span className="text-[12px] font-normal text-[hsl(var(--text-muted))]">· {rev.totals?.paying_customers || 0} customers</span></div>
            </div>
            <Button variant="secondary" className="ml-auto" onClick={() => nav("/revenue")} data-testid="view-full-revenue-btn">
              <TrendingUp className="w-4 h-4" strokeWidth={1.8}/>Revenue dashboard<ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8}/>
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {items.map((p) => (
          <Card key={p.id} className="overflow-hidden" data-testid="product-card">
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                {p.icon_url
                  ? <img src={p.icon_url} alt="" className="w-12 h-12 rounded-lg object-cover ring-1 ring-[hsl(var(--border))]"/>
                  : <div className="w-12 h-12 rounded-lg bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] grid place-items-center font-bold text-lg">{(p.name || "?").slice(0, 2).toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{p.name}</h3>
                    <StatusPill variant={p.status === "active" ? "success" : p.status === "beta" ? "primary" : "default"}>{p.status}</StatusPill>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] mt-0.5">{KINDS.find((k) => k.v === p.kind)?.l || p.kind}{p.slug && ` · /${p.slug}`}</div>
                </div>
                <div className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditing(p)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]" title="Edit" data-testid="edit-product-btn"><Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                  <button onClick={() => onDelete(p)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600" title="Delete" data-testid="delete-product-btn"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                </div>
              </div>
              {p.description && <p className="text-[13px] text-[hsl(var(--text-muted))] leading-relaxed">{p.description}</p>}
              <div className="grid grid-cols-1 gap-2 text-[12px]">
                {p.deployment_url && <a href={p.deployment_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-[hsl(var(--primary))] hover:underline truncate"><Server className="w-3.5 h-3.5" strokeWidth={1.6}/>{p.deployment_url}<ExternalLink className="w-3 h-3" strokeWidth={1.6}/></a>}
                {p.repo_url && <a href={p.repo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] truncate"><Github className="w-3.5 h-3.5" strokeWidth={1.6}/>{p.repo_url}</a>}
                {p.docs_url && <a href={p.docs_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] truncate"><BookOpen className="w-3.5 h-3.5" strokeWidth={1.6}/>{p.docs_url}</a>}
              </div>
              {(p.tech_stack || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {p.tech_stack.map((t) => <span key={t} className="px-2 py-0.5 text-[10px] font-semibold rounded bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]">{t}</span>)}
                </div>
              )}
              <div className="rounded-lg border border-dashed border-[hsl(var(--border))] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold inline-flex items-center gap-1.5"><Users className="w-3 h-3" strokeWidth={2}/>Customer allocations · {(p.allocations || []).length}</div>
                  <button onClick={() => setAllocating(p)} className="text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline" data-testid="allocate-product-btn">+ Allocate</button>
                </div>
                {(p.allocations || []).length === 0 ? (
                  <div className="text-[11px] text-[hsl(var(--text-muted))] italic">No customers allocated yet. Defaults: {gbp(p.default_setup_fee)} setup + {gbp(p.default_monthly_fee)}/mo <span className="not-italic">(ex VAT)</span></div>
                ) : (
                  <div className="space-y-1.5">
                    {p.allocations.map((a) => (
                      <div key={a.customer_id} className="flex items-center justify-between text-[12px] py-1" data-testid="product-allocation-row">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{a.customer_name}</div>
                          {a.custom_url && <a href={a.custom_url} target="_blank" rel="noreferrer" className="text-[10px] text-[hsl(var(--primary))] hover:underline truncate inline-flex items-center gap-1">{a.custom_url}<ExternalLink className="w-2.5 h-2.5" strokeWidth={1.6}/></a>}
                        </div>
                        <div className="font-mono text-[11px] mr-2">
                          <span>{gbp(a.setup_fee)}</span>
                          {a.monthly_fee > 0 && <span className="text-[hsl(var(--primary))] ml-1">+ {gbp(a.monthly_fee)}/mo</span>}
                        </div>
                        <StatusPill variant={a.status === "active" ? "success" : a.status === "trial" ? "primary" : "default"}>{a.status}</StatusPill>
                        <button onClick={() => removeAllocation(p, a)} className="ml-2 p-1 rounded hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600" title="Remove"><X className="w-3 h-3" strokeWidth={2}/></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="lg:col-span-2"><Empty title="No products yet" subtitle="Click 'Add software' to register your first product." /></div>}
      </div>

      {showNew && <ProductFormModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <ProductFormModal product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {allocating && <AllocateModal product={allocating} customers={customers} onClose={() => setAllocating(null)} onSaved={() => { setAllocating(null); load(); }} />}
    </div>
  );
}

function ProductFormModal({ product, onClose, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState(() => ({
    name: product?.name || "",
    slug: product?.slug || "",
    description: product?.description || "",
    deployment_url: product?.deployment_url || "",
    staging_url: product?.staging_url || "",
    repo_url: product?.repo_url || "",
    docs_url: product?.docs_url || "",
    kind: product?.kind || "saas_app",
    status: product?.status || "active",
    tech_stack: (product?.tech_stack || []).join(", "),
    icon_url: product?.icon_url || "",
    default_setup_fee: product?.default_setup_fee ?? 0,
    default_monthly_fee: product?.default_monthly_fee ?? 0,
    revenue_share_pct: product?.revenue_share_pct ?? 0,
    notes: product?.notes || "",
  }));
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tech_stack: form.tech_stack.split(",").map((t) => t.trim()).filter(Boolean),
        default_setup_fee: parseFloat(form.default_setup_fee) || 0,
        default_monthly_fee: parseFloat(form.default_monthly_fee) || 0,
        revenue_share_pct: parseFloat(form.revenue_share_pct) || 0,
      };
      if (isEdit) { await api.patch(`/products/${product.id}`, payload); toast.success("Product updated"); }
      else { await api.post("/products", payload); toast.success("Product added"); }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-3xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="product-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isEdit ? `Edit ${product.name}` : "Add software"}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Name *</Label><Input required value={form.name} onChange={s("name")} placeholder="Vantage" data-testid="product-name"/></div>
          <div><Label>Slug (short id)</Label><Input value={form.slug} onChange={s("slug")} placeholder="vantage"/></div>
          <div><Label>Kind</Label><Select value={form.kind} onChange={s("kind")}>{KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}</Select></div>
          <div className="md:col-span-2"><Label>Short description</Label><Textarea rows={2} value={form.description} onChange={s("description")} placeholder="What does this product do?"/></div>
          <div><Label>Deployment URL</Label><Input value={form.deployment_url} onChange={s("deployment_url")} placeholder="https://app.vantage.io"/></div>
          <div><Label>Staging URL</Label><Input value={form.staging_url} onChange={s("staging_url")} placeholder="https://staging.vantage.io"/></div>
          <div><Label>Repo URL</Label><Input value={form.repo_url} onChange={s("repo_url")} placeholder="https://github.com/…"/></div>
          <div><Label>Docs URL</Label><Input value={form.docs_url} onChange={s("docs_url")} placeholder="https://docs.vantage.io"/></div>
          <div><Label>Icon URL</Label><Input value={form.icon_url} onChange={s("icon_url")} placeholder="https://…/icon.png"/></div>
          <div><Label>Status</Label>
            <Select value={form.status} onChange={s("status")}>
              <option value="alpha">Alpha</option>
              <option value="beta">Beta</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Tech stack (comma-separated)</Label><Input value={form.tech_stack} onChange={s("tech_stack")} placeholder="React, FastAPI, Postgres, Stripe"/></div>
          <div>
            <Label>Default setup fee (£, ex VAT)</Label>
            <Input type="number" min="0" step="0.01" value={form.default_setup_fee} onChange={s("default_setup_fee")} placeholder="5000"/>
            {form.default_setup_fee > 0 && <div className="text-[10px] text-[hsl(var(--text-muted))] mt-1">Customer will see {gbp(parseFloat(form.default_setup_fee || 0) * 1.2)} inc 20% VAT</div>}
          </div>
          <div>
            <Label>Default monthly fee (£/mo, ex VAT)</Label>
            <Input type="number" min="0" step="0.01" value={form.default_monthly_fee} onChange={s("default_monthly_fee")} placeholder="200"/>
            {form.default_monthly_fee > 0 && <div className="text-[10px] text-[hsl(var(--text-muted))] mt-1">Customer will see {gbp(parseFloat(form.default_monthly_fee || 0) * 1.2)}/mo inc 20% VAT</div>}
          </div>
          <div><Label>Revenue share (%)</Label><Input type="number" min="0" max="100" step="1" value={form.revenue_share_pct} onChange={s("revenue_share_pct")} placeholder="0"/></div>
          <div className="md:col-span-2"><Label>Notes (internal)</Label><Textarea rows={2} value={form.notes} onChange={s("notes")} placeholder="Internal notes. Onboarding playbook, contacts, etc."/></div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="product-save-btn">{saving ? "Saving…" : isEdit ? "Save changes" : "Add product"}</Button>
        </div>
      </form>
    </div>
  );
}

function AllocateModal({ product, customers, onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_id: "",
    custom_url: "",
    setup_fee: product.default_setup_fee || 0,
    monthly_fee: product.default_monthly_fee || 0,
    status: "active",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const allocated = new Set((product.allocations || []).map((a) => a.customer_id));
  const eligible = customers.filter((c) => !allocated.has(c.id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Pick a customer"); return; }
    setSaving(true);
    try {
      await api.post(`/products/${product.id}/allocations`, {
        ...form,
        setup_fee: parseFloat(form.setup_fee) || 0,
        monthly_fee: parseFloat(form.monthly_fee) || 0,
      });
      toast.success("Customer allocated");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl" data-testid="allocate-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">Allocate {product.name}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label>Customer *</Label>
            <Select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="alloc-customer">
              <option value="">Select…</option>
              {eligible.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
            </Select>
            {eligible.length === 0 && <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">All customers already allocated.</div>}
          </div>
          <div>
            <Label>Custom URL (optional)</Label>
            <Input value={form.custom_url} onChange={(e) => setForm({ ...form, custom_url: e.target.value })} placeholder={product.deployment_url || "https://customer.vantage.io"}/>
            <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">Leave blank to use the product's default deployment URL.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Setup fee (£)</Label><Input type="number" min="0" step="0.01" value={form.setup_fee} onChange={(e) => setForm({ ...form, setup_fee: e.target.value })}/></div>
            <div><Label>Monthly fee (£/mo)</Label><Input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}/></div>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Contract refs, payment cadence, etc."/></div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving || eligible.length === 0} data-testid="alloc-save-btn">{saving ? "Saving…" : "Allocate"}</Button>
        </div>
      </form>
    </div>
  );
}
